import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { CloseButton, Modal, Chip } from '@spotahome/ui-library';
import classNames from 'classnames';

import { isSmallScreen } from '@spotahome/marketplace-common/src/utils/screenWidth';

import './DropdownButton.scss';

const DropdownButton = ({
  className,
  dataTest,
  isOpen,
  isSelected,
  onToggle,
  onClose,
  renderButtonContent,
  renderDropdownContent,
  isRightToLeft
}) => {
  const parent = useRef();

  useEffect(() => {
    const closeOnClick = e => {
      if (isOpen && parent.current && !parent.current.contains(e.target)) {
        onClose();
      }
    };

    if (!isSmallScreen()) {
      document.addEventListener('click', closeOnClick);
    }

    return () => document.removeEventListener('click', closeOnClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const renderModal = () => (
    <Modal
      size="fullscreen"
      className="dropdown-button"
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      <CloseButton
        dataTest="dropdown-button-close"
        className="dropdown-button__close"
        onClick={onClose}
      />
      {renderDropdownContent(onClose)}
    </Modal>
  );

  const dropdownClassses = classNames('dropdown-button__dropdown', {
    'dropdown-button__dropdown--right-to-left': isRightToLeft
  });

  const renderDropdown = () =>
    isOpen ? (
      <div
        ref={parent}
        data-test="dropdown-button-dropdown"
        className={dropdownClassses}
      >
        {renderDropdownContent(onClose)}
      </div>
    ) : null;

  const containerClassNames = classNames('dropdown-button', className);

  return (
    <div className={containerClassNames}>
      <Chip.Filter
        className="dropdown-button dropdown-button__chip"
        showChevron
        isSelected={isSelected}
        label={renderButtonContent()}
        isOpen={isOpen}
        onClick={onToggle}
        dataTest={dataTest}
      />
      {isSmallScreen() ? renderModal() : renderDropdown()}
    </div>
  );
};

DropdownButton.propTypes = {
  renderDropdownContent: PropTypes.func.isRequired,
  renderButtonContent: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool,
  isRightToLeft: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  className: PropTypes.string,
  dataTest: PropTypes.string
};

DropdownButton.defaultProps = {
  className: '',
  isSelected: false,
  onClose: () => {},
  dataTest: 'dropdown-button-toggle',
  isRightToLeft: false
};

export default DropdownButton;
